import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "~/components/ui/form";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "~/components/ui/input-otp";
import { useAuth } from "~/lib/hooks/useAuth";
import { cn } from "~/lib/utils";

const formSchema = z.object({
	otp: z
		.string()
		.length(6, "Please enter a valid OTP")
		.regex(/^[0-9]+$/, "OTP must contain only numbers"),
});

const searchSchema = z.object({
	email: z.string().email("Please enter a valid email address"),
});

function Verify() {
	const navigate = useNavigate();
	const email = Route.useSearch({
		select: (search) => search.email,
	});

	// If no phone number is provided, redirect to login
	if (!email) {
		navigate({ to: "/login" });
		return null;
	}

	const { verifyOTP } = useAuth();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			otp: "",
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		await verifyOTP.mutateAsync({
			email: email.toString(),
			otp: values.otp,
		});
	}

	return (
		<div className="min-h-screen w-full bg-black flex items-center justify-center p-4 sm:px-6 lg:px-8">
			<div className="w-full max-w-md">
				<div className="text-center mb-10">
					<h1 className="text-5xl sm:text-7xl leading-none font-black text-transparent bg-clip-text bg-gradient-to-br from-white to-accent/80 tracking-wide font-heading">
						meteor
					</h1>
					<p className="text-secondary uppercase tracking-widest mt-3 font-medium">
						Make memories
					</p>
				</div>
				<Card className="bg-primary/10 border-white/10 backdrop-blur-sm shadow-2xl shadow-accent/20">
					<CardHeader className="space-y-2 pt-8 pb-4">
						<h2 className="text-2xl font-semibold tracking-tight text-center text-primary-foreground">
							Verify OTP
						</h2>
						<p className="text-base text-secondary text-center">
							Enter the 6-digit code sent to {email}
						</p>
					</CardHeader>
					<CardContent className="space-y-6 pb-8 px-8">
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit)}
								className="space-y-6"
							>
								<FormField
									control={form.control}
									name="otp"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<InputOTP
													maxLength={6}
													value={field.value}
													onChange={field.onChange}
													pattern={REGEXP_ONLY_DIGITS}
													containerClassName="justify-center"
													placeholder="000000"
													className={cn(
														"bg-primary/10 border-white/10 text-white focus:border-accent/50 focus:ring-accent/20",
														verifyOTP.isPending && "opacity-50",
													)}
												>
													<InputOTPGroup>
														<InputOTPSlot
															index={0}
															className={cn(
																"bg-primary/10 border-white/10 text-white focus:border-accent/50 focus:ring-accent/20",
																verifyOTP.isPending && "opacity-50",
															)}
														/>
														<InputOTPSlot
															index={1}
															className={cn(
																"bg-primary/10 border-white/10 text-white focus:border-accent/50 focus:ring-accent/20",
																verifyOTP.isPending && "opacity-50",
															)}
														/>
														<InputOTPSlot
															index={2}
															className={cn(
																"bg-primary/10 border-white/10 text-white focus:border-accent/50 focus:ring-accent/20",
																verifyOTP.isPending && "opacity-50",
															)}
														/>
													</InputOTPGroup>
													<InputOTPSeparator className="text-secondary" />
													<InputOTPGroup>
														<InputOTPSlot
															index={3}
															className={cn(
																"bg-primary/10 border-white/10 text-white focus:border-accent/50 focus:ring-accent/20",
																verifyOTP.isPending && "opacity-50",
															)}
														/>
														<InputOTPSlot
															index={4}
															className={cn(
																"bg-primary/10 border-white/10 text-white focus:border-accent/50 focus:ring-accent/20",
																verifyOTP.isPending && "opacity-50",
															)}
														/>
														<InputOTPSlot
															index={5}
															className={cn(
																"bg-primary/10 border-white/10 text-white focus:border-accent/50 focus:ring-accent/20",
																verifyOTP.isPending && "opacity-50",
															)}
														/>
													</InputOTPGroup>
												</InputOTP>
											</FormControl>
											<FormMessage className="mt-2 text-red-500 text-sm" />
										</FormItem>
									)}
								/>
								<div className="space-y-4">
									<Button
										type="submit"
										className="w-full h-10 text-lg bg-accent hover:bg-accent/90 text-accent-foreground"
										disabled={verifyOTP.isPending}
									>
										{verifyOTP.isPending ? "Verifying..." : "Verify"}
									</Button>
									{/* <div className="text-center">
										<Button
											type="button"
											variant="link"
											className="text-sm text-secondary hover:text-primary-foreground"
										>
											Didn't receive code? Send again
										</Button>
									</div> */}
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/_auth/verify")({
	component: Verify,
	validateSearch: (search: Record<string, unknown>) => {
		return searchSchema.parse(search);
	},
});
